import React from 'react';
import { RateSchedule } from '@/types';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectTspOptions } from '../map/slice/mapSelectors';
import { useGetTSPsQuery } from '../services/edgeApi';
import { HelpCircleIcon } from '@iconicicons/react';
import { UncontrolledTooltip } from 'reactstrap';

export type RateScheduleFormProps = {
  defaultValues?: Partial<RateSchedule>;
  onSubmit: (data: Partial<RateSchedule>) => void;
};

export const RateScheduleForm: React.FC<RateScheduleFormProps> = ({
  defaultValues,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm<Partial<RateSchedule>>({
    mode: 'onChange',
    defaultValues,
  });

  useGetTSPsQuery();

  const tspOptions = useSelector(selectTspOptions);

  watch();

  return (
    <form className="form" onSubmit={handleSubmit(onSubmit)}>
      <div className="row my-4">
        <div className="col-6">
          <label htmlFor="tsp-form-select" className="form-label">
            Pipeline *
          </label>
          <select
            className={`form-select ${errors.tsp_id && 'is-invalid'}`}
            id="tsp-form-select"
            defaultValue={undefined}
            {...register('tsp_id', {
              required: true,
              min: 1,
              valueAsNumber: true,
            })}
          >
            <option value={''}>Select...</option>
            {tspOptions.map((tsp) => (
              <option key={tsp.value} value={tsp.value}>
                {tsp.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6">
          <label htmlFor="name-form-input" className="form-label">
            Name *
          </label>
          <input
            type="text"
            className={`form-control ${errors.name && ' is-invalid'}`}
            id="name-form-input"
            placeholder="Rate Schedule Name"
            {...register('name', {
              required: true,
              minLength: 2,
            })}
          />
        </div>
      </div>
      <div className="row my-4">
        <div className="col-6">
          <label htmlFor="service-form-select" className="form-label">
            Service *
          </label>
          <select
            className={`form-select ${errors.service && ' is-invalid'}`}
            id="service-form-select"
            defaultValue={undefined}
            {...register('service', {
              required: true,
              minLength: 1,
            })}
          >
            <option value={''}>Select...</option>
            <option value="FT">Firm</option>
            <option value="IT">IT</option>
          </select>
        </div>
        <div className="col-6">
          <label htmlFor="category-form-select" className="form-label">
            Category *
          </label>
          <select
            className={`form-select ${errors.category && ' is-invalid'}`}
            id="category-form-select"
            defaultValue={undefined}
            {...register('category', {
              required: true,
              minLength: 1,
            })}
          >
            <option value={''}>Select...</option>
            <option value="FUEL">Fuel</option>
            <option value="COMMODITY">Commodity</option>
          </select>
        </div>
      </div>
      <div className="row my-4">
        <div className="col-12">
          <label htmlFor="description-form-input" className="form-label">
            Description
          </label>
          <textarea
            className="form-control"
            id="description-form-input"
            placeholder="Rate Schedule Description"
            rows={3}
            {...register('description')}
          />
        </div>
      </div>
      <div className="row my-4">
        <div className="col-12">
          <label
            htmlFor="is-strict-form-input"
            className="form-label d-flex gap-2"
          >
            Strict{' '}
            <span
              id="strict-help"
              className="text-muted d-flex align-items-center"
            >
              <HelpCircleIcon />
            </span>
            <UncontrolledTooltip target="strict-help" placement="top">
              Enabling strict mode will prevent optimizer from using default
              rates if no rate is found for a given point pair.
            </UncontrolledTooltip>
          </label>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="is-strict-form-input"
              {...register('is_strict')}
            />
          </div>
        </div>
      </div>
      <button type="submit" className="btn btn-success" disabled={!isValid}>
        Create
      </button>
    </form>
  );
};
